import {
  BarChart3,
  ChevronDown,
  Database,
  FileText,
  GitCompare,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Server,
  ShoppingCart,
  User as UserIcon,
  Users,
  Wallet,
} from "lucide-react";
import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { AuthModeBadge } from "@/components/AuthModeIndicator";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { useConnection } from "@/auth/ConnectionContext";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  permission?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Панель", icon: LayoutDashboard },
  { to: "/invoices", label: "Счета-фактуры", icon: FileText, permission: "invoices" },
  {
    to: "/mp-reports",
    label: "Отчёты МП",
    icon: BarChart3,
    permission: "marketplace_reports",
  },
  {
    to: "/mp-buyouts",
    label: "Выкупы МП",
    icon: ShoppingCart,
    permission: "marketplace_buyouts",
  },
  {
    to: "/intercompany",
    label: "Сверка интеркампани",
    icon: GitCompare,
    permission: "intercompany_recon",
  },
  { to: "/cashflow", label: "Финплан", icon: Wallet, permission: "cashflow" },
  { to: "/admin/onec-bases", label: "Базы 1С (SSO)", icon: Server, adminOnly: true },
  { to: "/admin/users", label: "Пользователи", icon: Users, adminOnly: true },
];

const STORAGE_KEY = "sidebar-collapsed";

export function Layout(): React.ReactElement {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true",
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!user) return false;
    if (item.adminOnly && !user.is_admin) return false;
    // Итоговый список прав уже приходит с бэка (явные + admin_implicit для
    // админа) — здесь без своего «админ = всё», см. ProtectedRoute.
    if (item.permission && !user.permissions.includes(item.permission)) return false;
    return true;
  });

  return (
    <div className="flex h-screen bg-background text-foreground">
      <aside
        className={cn(
          "flex shrink-0 flex-col border-r bg-card transition-[width] duration-200",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <div className="flex h-14 items-center border-b px-4">
          {collapsed ? (
            <span className="mx-auto text-lg font-bold">Б</span>
          ) : (
            <span className="text-lg font-semibold">Buh Helper</span>
          )}
        </div>

        <nav className="flex-1 space-y-1 p-2">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  collapsed && "justify-center px-0",
                  isActive
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )
              }
            >
              <item.icon className="size-4 shrink-0" />
              {!collapsed && item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-1 border-t p-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            title={collapsed ? "Развернуть меню" : "Свернуть меню"}
          >
            {collapsed ? (
              <PanelLeftOpen className="mx-auto size-4" />
            ) : (
              <>
                <PanelLeftClose className="size-4 shrink-0" />
                Свернуть
              </>
            )}
          </button>

          {collapsed ? (
            <div className="flex justify-center py-1">
              <AuthModeBadge mode={user?.auth_mode} />
            </div>
          ) : (
            <div className="px-3 py-1 text-xs">
              {/* Логин рядом с плашкой режима входа — как в wh-helper;
                  `min-w-0` даёт логину обрезаться троеточием вместо того,
                  чтобы раздвигать сайдбар при длинном имени. */}
              <div className="flex items-center gap-2 font-medium text-foreground">
                <span className="min-w-0 truncate">{user?.username}</span>
                <AuthModeBadge mode={user?.auth_mode} />
              </div>
              {user?.is_admin && (
                <div className="text-muted-foreground">администратор</div>
              )}
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full",
              collapsed ? "justify-center px-0" : "justify-start",
            )}
            onClick={handleLogout}
            title="Выйти"
          >
            <LogOut className="size-4 shrink-0" />
            {!collapsed && <span className="ml-2">Выйти</span>}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full",
              collapsed ? "justify-center px-0" : "justify-start",
            )}
            asChild
            title="Профиль"
          >
            <NavLink to="/profile">
              <UserIcon className="size-4 shrink-0" />
              {!collapsed && <span className="ml-2">Профиль</span>}
            </NavLink>
          </Button>
        </div>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <ConnectionSelector />
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function ConnectionSelector(): React.ReactElement | null {
  const { connections, activeConnection, setActiveConnectionId } =
    useConnection();
  const [open, setOpen] = useState(false);

  if (connections.length <= 1) return null;

  return (
    <div className="relative border-b bg-card px-4 py-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-accent/50"
      >
        <Database className="size-4 text-muted-foreground" />
        <span className="font-medium">
          {activeConnection?.name ?? "Выберите подключение"}
        </span>
        {activeConnection?.config_type && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {activeConnection.config_type.toUpperCase()}
          </span>
        )}
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute left-4 top-full z-50 mt-1 w-72 rounded-md border bg-card shadow-lg">
          {connections.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setActiveConnectionId(c.id);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent/50",
                c.id === activeConnection?.id && "bg-accent/30",
              )}
            >
              <Database className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{c.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.config_type.toUpperCase()} · {c.odata_url.replace(/^https?:\/\//, "").split("/")[0]}
                </div>
              </div>
              {c.id === activeConnection?.id && (
                <span className="text-xs text-green-700">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
